import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { OnboardingData } from "../index";

interface WelcomeProps {
  onNext: () => void;
  onboardingData: OnboardingData;
  updateOnboardingData: (data: Partial<OnboardingData>) => void;
}

export default function Welcome({
  onNext,
  onboardingData,
  updateOnboardingData,
}: WelcomeProps) {
  // Update onboarding start time if it doesn't exist
  const handleStart = () => {
    if (!onboardingData.startedAt) {
      updateOnboardingData({
        startedAt: new Date()
      });
    }
    onNext();
  };
  
  return (
    <div className="flex flex-col items-center justify-center max-w-3xl mx-auto text-center">
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-primary to-primary/70 text-transparent bg-clip-text">
            Simplify Your Finances with Professional Bookkeeping
          </h1>
          <p className="mt-4 text-lg text-neutral-600">
            Get started in minutes. No credit card required.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
          <div className="p-6 bg-white rounded-lg border border-neutral-200 shadow-sm">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-medium">Setup in Minutes</h3>
            <p className="mt-2 text-sm text-neutral-600">
              Quick and easy setup process to get your business finances organized
            </p>
          </div>
          
          <div className="p-6 bg-white rounded-lg border border-neutral-200 shadow-sm">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M16 10h.01"></path>
                <path d="M8 10h.01"></path>
                <path d="M15 15a3 3 0 0 1-6 0"></path>
              </svg>
            </div>
            <h3 className="text-lg font-medium">AI-Powered</h3>
            <p className="mt-2 text-sm text-neutral-600">
              Automatic transaction categorization and financial insights
            </p>
          </div>
          
          <div className="p-6 bg-white rounded-lg border border-neutral-200 shadow-sm">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
              </svg>
            </div>
            <h3 className="text-lg font-medium">Secure & Compliant</h3>
            <p className="mt-2 text-sm text-neutral-600">
              Bank-level security and audit-ready financials
            </p>
          </div>
        </div>
        
        <div className="bg-white p-6 border border-neutral-200 rounded-lg mt-8">
          <h3 className="text-lg font-medium mb-4">What You'll Need</h3>
          <ul className="space-y-3">
            <li className="flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600 mr-2 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              <span>Basic information about your business</span>
            </li>
            <li className="flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600 mr-2 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              <span>Bank account login (optional but recommended)</span>
            </li>
            <li className="flex items-start">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-600 mr-2 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              <span>5 minutes to complete the setup</span>
            </li>
          </ul>
        </div>
        
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              onClick={handleStart}
              size="lg"
              className="w-full sm:w-auto px-8 py-6 h-auto text-base rounded-md bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
            >
              Sign Up Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            
            <Button 
              variant="outline"
              size="lg"
              onClick={() => window.open('mailto:hello@zentrobooks.com?subject=Schedule a Demo')}
              className="w-full sm:w-auto px-8 py-6 h-auto text-base rounded-md"
            >
              Schedule a Demo
            </Button>
          </div>
          
          <p className="text-neutral-500 text-sm">
            By continuing, you agree to our{" "}
            <a href="#" className="text-primary underline hover:text-primary/80">
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="#" className="text-primary underline hover:text-primary/80">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}